
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Image, Upload, X, FileVideo } from "lucide-react";
import { useNavigate } from "react-router-dom";

// Sample categories from the BlogPage
const CATEGORIES = ["技術", "旅行", "アドバイス", "生活", "ガイド"];

export function BlogPostEditor({ post = null, onPostSaved = () => {} }: { post?: any; onPostSaved?: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    title: post?.title || "",
    description: post?.description || "",
    content: post?.content || "",
    category: post?.category || CATEGORIES[0],
    featured: post?.featured || false
  });
  
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>(post?.image_url || "");
  const [videoPreview, setVideoPreview] = useState<string>(post?.video_url || "");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const imageUrl = URL.createObjectURL(file);
      setImagePreview(imageUrl);
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setVideoFile(file);
      const videoUrl = URL.createObjectURL(file);
      setVideoPreview(videoUrl);
    }
  };

  const clearImage = () => {
    setImageFile(null);
    setImagePreview("");
  };

  const clearVideo = () => {
    setVideoFile(null);
    setVideoPreview("");
  };

  const uploadFile = async (file: File, fileType: 'image' | 'video') => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2, 15)}_${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    const filePath = `${fileType}s/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('blog_media')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) {
      throw uploadError;
    }

    const { data } = supabase.storage
      .from('blog_media')
      .getPublicUrl(filePath);

    return data.publicUrl;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      let imageUrl = imagePreview;
      let videoUrl = videoPreview;

      // Upload image if a new one is selected
      if (imageFile) {
        imageUrl = await uploadFile(imageFile, 'image');
      }

      // Upload video if a new one is selected
      if (videoFile) {
        videoUrl = await uploadFile(videoFile, 'video');
      }

      const postData = {
        title: formData.title,
        description: formData.description,
        content: formData.content,
        category: formData.category,
        featured: formData.featured,
        image_url: imageUrl || null,
        video_url: videoUrl || null
      };

      let result;

      if (post?.id) {
        // Update existing post
        result = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', post.id)
          .select()
          .single();
      } else {
        // Create new post
        result = await supabase
          .from('blog_posts')
          .insert(postData)
          .select()
          .single();
      }

      if (result.error) throw result.error;
      
      toast.success(post 
        ? t('blog.admin.postUpdated') 
        : t('blog.admin.postCreated')
      );
      
      // Call the callback function to notify parent component
      onPostSaved();
      
      // If this is a new post, reset the form
      if (!post) {
        setFormData({
          title: "",
          description: "",
          content: "",
          category: CATEGORIES[0],
          featured: false
        });
        setImageFile(null);
        setVideoFile(null);
        setImagePreview("");
        setVideoPreview("");
      }
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(t('blog.admin.saveFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">{t('blog.admin.postTitle')}</Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder={t('blog.admin.postTitlePlaceholder')}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description">{t('blog.admin.postDescription')}</Label>
            <Textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={t('blog.admin.postDescriptionPlaceholder')}
              rows={3}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="content">{t('blog.admin.postContent')}</Label>
            <Textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleChange}
              placeholder={t('blog.admin.postContentPlaceholder')}
              rows={10}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('blog.admin.postImage')}</Label>
              {imagePreview ? (
                <div className="relative border rounded-md overflow-hidden h-40">
                  <img 
                    src={imagePreview} 
                    alt={formData.title}
                    className="w-full h-full object-cover"
                  />
                  <Button 
                    variant="destructive" 
                    size="icon" 
                    className="absolute top-2 right-2"
                    onClick={clearImage}
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center border border-dashed rounded-md p-6 h-40">
                  <label className="flex flex-col items-center justify-center cursor-pointer">
                    <Image className="mb-2 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{t('blog.admin.clickToUploadImage')}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                  </label>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>{t('blog.admin.postVideo')}</Label>
              {videoPreview ? (
                <div className="relative border rounded-md overflow-hidden h-40">
                  <video 
                    src={videoPreview} 
                    controls
                    className="w-full h-full object-cover"
                  />
                  <Button 
                    variant="destructive" 
                    size="icon" 
                    className="absolute top-2 right-2"
                    onClick={clearVideo}
                    type="button"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center justify-center border border-dashed rounded-md p-6 h-40">
                  <label className="flex flex-col items-center justify-center cursor-pointer">
                    <FileVideo className="mb-2 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{t('blog.admin.clickToUploadVideo')}</span>
                    <input
                      type="file"
                      accept="video/*"
                      className="hidden"
                      onChange={handleVideoChange}
                    />
                  </label>
                </div>
              )}
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="category">{t('blog.admin.postCategory')}</Label>
            <Select 
              value={formData.category}
              onValueChange={(value) => handleSelectChange('category', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('blog.admin.selectCategory')} />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(category => (
                  <SelectItem key={category} value={category}>
                    {t(`blog.categories.${category.toLowerCase()}`) || category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="featured"
              name="featured"
              checked={formData.featured}
              onChange={handleCheckboxChange}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <Label htmlFor="featured" className="cursor-pointer">
              {t('blog.admin.markAsFeatured')}
            </Label>
          </div>
          
          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('common.saving') : (post ? t('common.update') : t('common.create'))}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
